import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PrescriptionMedicationTextBoxes } from "@/Common/Models/Patient/PatientEnums";
import { MedicationDosageDisplayMode, MedicationDosageDisplayModeDurationUnit } from "@/Patient/Models/Medication/MedicationModel";

import styles from "./DosageDurationOrQuantityEditForm.module.scss";

interface IDosageDurationOrQuantityEditFormProps {
    medicalItemId: string;
    currentDosageId: string;
    currentDosageDisplayMode: MedicationDosageDisplayMode;
    currentDosageDisplayModeDurationUnit: MedicationDosageDisplayModeDurationUnit;
    currentDosageNarcoticServedQuantity: string;
    numberOfDays: number;
    isNarcotic: boolean;
    onDosageDurationDaysClick: () => void;
    onDosageDurationWeeksClick: () => void;
    onDosageDurationMonthsClick: () => void;
    onDosageQuantityClick: () => void;
    onDosageDurationChange: (newText: string) => void;
    onDosageQuantityChange: (newText: string) => void;

}

export function DosageDurationOrQuantityEditForm(props: IDosageDurationOrQuantityEditFormProps): JSX.Element {
    const { getText } = useTranslation();

    const narcoticServedQuantity = props.currentDosageNarcoticServedQuantity ?? "";

    const hyperLinkTemplate = (label: string, onClick: () => void): JSX.Element => (
        <div className="editablecell">
            <a
                onClick={() => onClick()}
                id={props.medicalItemId}
            > {getText(label)}
            </a>
        </div>
    );

    const selectedLinkTemplate = (label: string): JSX.Element => (
        <div className="editablecell"><span>{getText(label)}</span></div>
    );

    const textBoxDurationOrQuantityTemplate = (txtId: string, value: any, targetField: PrescriptionMedicationTextBoxes, onChange: (newText: string) => void): JSX.Element => (
        <div className="editablecell">
            <TextboxControl
                IsSmall={true}
                IsNumeric={targetField === PrescriptionMedicationTextBoxes.DosageDuration}
                maxLength={4}
                className={`form-control input ${styles.durationOrQuantityTextbox}`}
                id={`${txtId}_${props.medicalItemId}_${props.currentDosageId}`}
                value={value}
                onChange={(newText: string) => onChange(newText)}
                dataCy="dosageDurationOrQuantityTextbox"
            />
        </div>
    );
    let textBoxDurationOrQuantity: JSX.Element = (<div />);

    let durationDaysHyperLink = hyperLinkTemplate("DaysLabel", props.onDosageDurationDaysClick);
    let durationWeeksHyperLink = hyperLinkTemplate("WeekLabel", props.onDosageDurationWeeksClick);
    let durationMonthsHyperLink = hyperLinkTemplate("MonthsLabel", props.onDosageDurationMonthsClick);
    let quantityHyperLink = hyperLinkTemplate("QuantityLabel", props.onDosageQuantityClick);

    if (props.currentDosageDisplayMode == MedicationDosageDisplayMode.Duration && props.currentDosageDisplayModeDurationUnit == MedicationDosageDisplayModeDurationUnit.Days) {
        durationDaysHyperLink = selectedLinkTemplate("DaysLabel");
        textBoxDurationOrQuantity = textBoxDurationOrQuantityTemplate("_days", props.numberOfDays, PrescriptionMedicationTextBoxes.DosageDuration, props.onDosageDurationChange);
    }
    if (props.currentDosageDisplayMode == MedicationDosageDisplayMode.Duration && props.currentDosageDisplayModeDurationUnit == MedicationDosageDisplayModeDurationUnit.Weeks) {
        durationWeeksHyperLink = selectedLinkTemplate("WeekLabel");
        textBoxDurationOrQuantity = textBoxDurationOrQuantityTemplate("_days", Math.round(props.numberOfDays / 7), PrescriptionMedicationTextBoxes.DosageDuration, props.onDosageDurationChange);
    }
    if (props.currentDosageDisplayMode == MedicationDosageDisplayMode.Duration && props.currentDosageDisplayModeDurationUnit == MedicationDosageDisplayModeDurationUnit.Months) {
        durationMonthsHyperLink = selectedLinkTemplate("MonthsLabel");
        textBoxDurationOrQuantity = textBoxDurationOrQuantityTemplate("_days", Math.round(props.numberOfDays / 30), PrescriptionMedicationTextBoxes.DosageDuration, props.onDosageDurationChange);
    }
    if (props.currentDosageDisplayMode == MedicationDosageDisplayMode.Quantity) {
        quantityHyperLink = selectedLinkTemplate("QuantityLabel");
        textBoxDurationOrQuantity = textBoxDurationOrQuantityTemplate("_quantity", narcoticServedQuantity, PrescriptionMedicationTextBoxes.DosageQuantity, props.onDosageQuantityChange);
    }

    return (
        <div className="flex-container meds" key={"DosageOrQty_" + props.medicalItemId + "_" + props.currentDosageId}>
            <div className="label">
                {props.currentDosageDisplayMode == MedicationDosageDisplayMode.Quantity && (<span>{getText("QuantityLabel")}</span>)}
                {props.currentDosageDisplayMode != MedicationDosageDisplayMode.Quantity && (<span>{getText("DurationLabel")}</span>)}
            </div>

            <div className={`flex-container meds ${styles.hyperlinkContainer}`}>
                <div className="flex-container meds">
                    <div className="flex-container meds">
                        {textBoxDurationOrQuantity}
                    </div>
                    {props.isNarcotic != true && (
                        <div className="flex-container meds">
                            {durationDaysHyperLink}
                            <div className="editablecell"><span>{" / "}</span></div>
                            {durationWeeksHyperLink}
                            <div className="editablecell"><span>{" / "}</span></div>
                            {durationMonthsHyperLink}
                            <div className="editablecell"><span>{" / "}</span></div>
                            {quantityHyperLink}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
